import React from "react";
import { ArrowUpRight, Quote, Newspaper } from "lucide-react";
import { useSite } from "../context/SiteContext";
import useReveal from "../hooks/useReveal";
import Crest from "./Crest";

function ClipCard({ item, index }) {
  const { c } = useSite();
  const { ui } = c;
  return (
    <a
      href={item.url}
      target="_blank"
      rel="noopener noreferrer"
      className="reveal group flex flex-col border border-[var(--c-border)] bg-ink2 hover:border-gold/50 transition-colors duration-300"
      style={{ animationDelay: `${(index % 3) * 90}ms` }}
    >
      {/* masthead strip (stylised, not a reproduction of the outlet's page) */}
      <div className="flex items-center justify-between border-b border-[var(--c-border)] px-6 py-4">
        <span className="flex items-center gap-2.5">
          <Newspaper className="w-4 h-4 text-gold/70" />
          <span className="font-display text-xl text-parchment tracking-wide">{item.outlet}</span>
        </span>
        <span className="font-label text-[0.58rem] uppercase tracking-[0.18em] text-stone2 border border-[var(--c-border)] px-2 py-1">
          {item.lang} · {item.date}
        </span>
      </div>

      <div className="px-6 py-6 flex-1 flex flex-col">
        <span className="font-label text-[0.58rem] uppercase tracking-[0.2em] text-gold">
          {item.region}
        </span>
        <h3 className="font-display text-2xl font-medium text-parchment leading-snug mt-2">
          {item.headline}
        </h3>

        <div className="relative mt-4 pl-6">
          <Quote className="absolute left-0 top-0.5 w-4 h-4 text-oxblood" />
          <p className="font-display italic text-lg text-parchment/80 leading-snug">
            “{item.quote}”
          </p>
        </div>

        <p className="mt-4 text-[0.95rem] leading-relaxed text-parchment/55 font-light">
          {item.summary}
        </p>

        <span className="mt-6 inline-flex items-center gap-1.5 font-label text-[0.62rem] uppercase tracking-[0.2em] text-gold/80 group-hover:text-gold transition-colors">
          {ui.readOriginal}
          <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </span>
      </div>
    </a>
  );
}

export default function PressPage() {
  useReveal();
  const { c } = useSite();
  const { press } = c;
  return (
    <div className="bg-ink">
      {/* header band */}
      <section className="pt-36 sm:pt-44 pb-16 border-b border-[var(--c-border)]">
        <div className="max-w-[1220px] mx-auto px-5 sm:px-8">
          <div className="flex items-center gap-4 mb-7">
            <span className="eyebrow">{press.label}</span>
          </div>
          <h1 className="font-display text-5xl sm:text-7xl font-light leading-[0.98] text-parchment max-w-3xl">
            {press.title}
          </h1>
          <p className="mt-7 text-lg sm:text-xl leading-relaxed text-parchment/65 font-light max-w-2xl">
            {press.intro}
          </p>
        </div>
      </section>

      {/* clippings */}
      <section className="py-20 sm:py-28">
        <div className="max-w-[1220px] mx-auto px-5 sm:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {press.clippings.map((item, i) => (
              <ClipCard key={item.id} item={item} index={i} />
            ))}
          </div>

          {/* legal / fair-use note */}
          <div className="mt-16 flex flex-col sm:flex-row items-start gap-5 border-t border-[var(--c-border)] pt-10">
            <Crest size={40} className="shrink-0 opacity-70" />
            <p className="text-sm text-parchment/45 font-light leading-relaxed max-w-3xl">
              {press.legalNote}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
